import React, { useEffect, useState } from 'react';
import { bookingShape, historyShape, passengerShape, userShape } from 'constants/propTypes';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useMutation, useLazyQuery } from '@apollo/client';
import moment from 'moment';

import { withUser } from 'components/queries/GetUserQuery';
import { availableTicketIssueOptions, issueTicketTypes } from 'constants/issueTickets';
import AlertBox from 'components/ui/AlertBox';
import { getSptmConfig } from 'utils/sptm';
import { ADD_ADDITIONAL_DETAILS } from 'store/mutations';
import Modal from 'components/ui/Modal/Modal';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import { isCanPrintOnMobile } from 'utils/isCanPrintOnMobile';

import GetAlertBox from './GetAlertBox';
import GenerateTickets from './GenerateTickets';
import { byIssueTicketType } from './mutations';
import { TICKET_TYPES } from '../../../types/ticket';
import { ConfirmButton } from '../../../components/ui/Button';
import { getBookingTravelDocumentsURL } from '../../../utils/urls';
import { withRouter } from 'react-router';
import { ButtonWrapper } from './GenerateTickets.styles';
import { getIsPassportRequired } from '../../../utils/ticket';
import getTickets from './queries/getTickets';
import { isEurostarOffer } from 'utils/isEurostarOffer';
import {
  getIsEROnlyJourney,
  getHasNonEurostarExpiredTickets,
  getEurostarBookingItems,
  getEurostarTickets,
  getNonEurostarUnexpiredTickets,
  getHasSNCBTicketsUnavailable
} from './GenerateTickets.utils';

const GenerateTicketsContainer = ({
  booking,
  disabled,
  bookingReference,
  passengers,
  passengerLastName,
  user,
  history,
  checkPassportRequirement
}) => {
  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const availableMethods = availableTicketIssueOptions[user.isStation ? 'stations' : 'other'];

  const handlePDFCompleted = ({ getTickets }) => {
    if (getTickets) {
      const openedTickets = new Set();

      const pdfUrls = getTickets.passengers
        ?.map((p) => p.tickets)
        .flat()
        .map((t) => t.ticketUrls.pdfUrl)
        .flat();

      for (let ticketURL of pdfUrls) {
        if (!openedTickets.has(ticketURL)) {
          openedTickets.add(ticketURL);
          window.open(ticketURL, '_blank');
        }
      }
    }
  };

  const [getTicketsQuery] = useLazyQuery(getTickets, {
    fetchPolicy: 'network-only',
    onCompleted: handlePDFCompleted
  });

  const [isOpen, setOpen] = useState(false);
  const [isPassportOpen, setIsPassportOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(availableMethods[0]);

  const [sptmConfig, setSptmConfig] = useState({
    TERMINAL_ID: null,
    TERMINAL_SERIAL_NO: null
  });

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);

  const [isMobileIndirect, setIsMobileIndirect] = useState(false);

  const passengerEmails = [...new Set(passengers.map(({ email }) => email).filter(Boolean))];
  const [selectedEmails, setSelectedEmails] = useState(passengerEmails);
  const passengerIds = [...new Set(passengers.map(({ id }) => id))];
  const market = booking?.market || 'uk-en';

  const getSptmConfigCall = async () => {
    if (user && user.isStation && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
      const { TERMINAL_ID, TERMINAL_SERIAL_NO } = await getSptmConfig();
      setSptmConfig({
        TERMINAL_ID,
        TERMINAL_SERIAL_NO
      });
    }
  };

  useEffect(() => {
    if (!sptmConfig.TERMINAL_ID) {
      getSptmConfigCall();
    }
  });

  const handleToggle = () => setOpen(!isOpen);
  const handleCompleted = ({ generateTicketsViaPDF }) => {
    if (selectedMethod === issueTicketTypes.DOWNLOAD_PDF) {
      generateTicketsViaPDF.forEach((url) => {
        window.open(url, '_blank');
      });
    }
  };

  const eurostarTickets = getEurostarTickets({ booking });
  const nonEurostarUnexpiredTickets = getNonEurostarUnexpiredTickets({ booking });
  const eurostarBookingItems = getEurostarBookingItems({ booking });
  const isEROnlyJourney = getIsEROnlyJourney({ booking });
  const hasNonEurostarExpiredTickets = getHasNonEurostarExpiredTickets({ booking });
  const hasSNCBTicketsUnavailable = getHasSNCBTicketsUnavailable({ booking });
  const ticketNumbers = [...eurostarTickets, ...nonEurostarUnexpiredTickets];

  let mutationVariables =
    selectedMethod === issueTicketTypes.PRINT_ADYEN_TERMINAL
      ? {
          bookingReference,
          cashRegisterId: sptmConfig.TERMINAL_SERIAL_NO,
          terminalId: sptmConfig.TERMINAL_ID
        }
      : {
          bookingReference,
          passengerLastName,
          language: booking.language || 'EN',
          itemRefs: eurostarBookingItems.map((i) => i.itemRef),
          emails: selectedEmails,
          combine: !isEROnlyJourney,
          passengerIds: passengerIds,
          market: market
        };

  if (selectedMethod === issueTicketTypes.DOWNLOAD_PDF) {
    mutationVariables = {
      bookingReference,
      lastName: passengerLastName,
      language: booking.language || 'EN',
      ticketNumbers: ticketNumbers
    };
  }

  const mutation = byIssueTicketType[selectedMethod];

  const [handlePress, result] = useMutation(mutation, {
    variables: mutationVariables,
    onCompleted: handleCompleted
  });

  const sncbTicketsUnavailable = hasSNCBTicketsUnavailable && selectedMethod === issueTicketTypes.DOWNLOAD_PDF;

  if (selectedMethod !== issueTicketTypes.DOWNLOAD_PDF && !byIssueTicketType[selectedMethod]) {
    return (
      <GenerateTickets
        bookingReference={bookingReference}
        disabled={disabled}
        isOpen={isOpen}
        onSelect={setSelectedMethod}
        onToggle={handleToggle}
        passengerLastName={passengerLastName}
        selectedMethod={selectedMethod}
        allowedMethods={availableMethods}
        passengerEmails={passengerEmails}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        hasNonEurostarExpiredTickets={hasNonEurostarExpiredTickets}
        hasSNCBTicketsUnavailable={sncbTicketsUnavailable}
      />
    );
  }

  const toggleModal = () => {
    setIsMobileIndirect(!isMobileIndirect);
  };

  const togglePassportModal = () => {
    setIsPassportOpen(!isPassportOpen);
  };

  if (result?.error && [issueTicketTypes.EMAIL_PDF, issueTicketTypes.DOWNLOAD_PDF].includes(selectedMethod)) {
    return (
      <AlertBox variant={AlertBox.variants.ERROR}>
        <FormattedMessage id="ticketMethods.s3Error" />
      </AlertBox>
    );
  }

  const onHandleCheck = async () => {
    let ticketType = TICKET_TYPES.EMAIL;

    switch (selectedMethod) {
      case issueTicketTypes.PRINT_ATB:
        ticketType = TICKET_TYPES.ATB;
        break;
      case issueTicketTypes.PRINT_ADYEN_TERMINAL:
        ticketType = TICKET_TYPES.QR;
        break;
      case issueTicketTypes.DOWNLOAD_PDF:
        ticketType = TICKET_TYPES.PDF;
        break;
      default:
        ticketType = TICKET_TYPES.EMAIL;
    }

    const isEsJourney = isEurostarOffer(booking.outbound.concat(booking.inbound));

    if (checkPassportRequirement && isEsJourney) {
      let isPassportRequired = getIsPassportRequired(user, booking, ticketType);

      if (isPassportRequired) {
        setOpen(false);
        setIsPassportOpen(true);

        return true;
      }
    }

    if (selectedMethod === issueTicketTypes.PRINT_ADYEN_TERMINAL) {
      const isCanPrint = isCanPrintOnMobile(booking);

      if (!isCanPrint) {
        setOpen(false);
        setIsMobileIndirect(true);
      } else {
        handlePress();

        addAdditionalDetails({
          variables: {
            bookingReference,
            additionalDetails: [
              {
                key: `tablet-print-${moment().format('HH:mm:ss.DDMMYY')}`,
                value: user.paymentLocation && user.paymentLocation.name
              }
            ]
          }
        });
      }
    } else if (selectedMethod === issueTicketTypes.DOWNLOAD_PDF) {
      getTicketsQuery({ variables: mutationVariables });
      return;
    } else {
      return handlePress();
    }

    return false;
  };

  const onCaptureTravelDocument = () => {
    history.push(getBookingTravelDocumentsURL(booking.reference, membershipNumber));
  };

  return (
    <>
      <Modal isOpen={isMobileIndirect} title="Error" onClose={toggleModal}>
        <FormattedMessage id="mobilePrint.noIndirect" />
      </Modal>

      <Modal isOpen={isPassportOpen} title="Error" onClose={togglePassportModal}>
        <FormattedMessage id="issueTicket.passportRequired1" />
        <br />
        <FormattedMessage id="issueTicket.passportRequired2" />
        <ButtonWrapper>
          <ConfirmButton variant="primary" onClick={onCaptureTravelDocument}>
            <FormattedMessage id="advancedPaxInfo.title" />
          </ConfirmButton>
        </ButtonWrapper>
      </Modal>
      <GenerateTickets
        alert={GetAlertBox({ success: !!result?.data, error: !!result?.error })}
        bookingReference={bookingReference}
        booking={booking}
        disabled={disabled}
        isOpen={isOpen}
        loading={result?.loading || addAdditionalDetailsLoading}
        onPress={onHandleCheck}
        onSelect={setSelectedMethod}
        onToggle={handleToggle}
        passengerLastName={passengerLastName}
        selectedMethod={selectedMethod}
        allowedMethods={availableMethods}
        passengerEmails={passengerEmails}
        selectedEmails={selectedEmails}
        setSelectedEmails={setSelectedEmails}
        hasNonEurostarExpiredTickets={hasNonEurostarExpiredTickets}
        hasSNCBTicketsUnavailable={sncbTicketsUnavailable}
      />
    </>
  );
};

GenerateTicketsContainer.propTypes = {
  user: userShape,
  booking: bookingShape,
  disabled: PropTypes.bool,
  bookingReference: PropTypes.string.isRequired,
  passengers: PropTypes.arrayOf(passengerShape),
  passengerLastName: PropTypes.string.isRequired,
  paxCount: PropTypes.number,
  checkPassportRequirement: PropTypes.bool,
  history: historyShape
};

export default withRouter(withUser(GenerateTicketsContainer));
